import { api } from "../api.js";
import { shell, pageHeading, statusPanel, hydrateCommitteeIdentity } from "../components.js";
import { escapeHtml, errorMessage, setSelectedCommittee } from "../utils.js";

let selectorsObserver;
let activeDrawerTrigger;

function normalizeSearch(value) {
  return String(value || "").trim().toLocaleLowerCase("it");
}

function fitGroupSelector(selector) {
  const chips = Array.from(selector.querySelectorAll("[data-group-chip]"));
  const more = selector.querySelector("[data-groups-more]");
  if (!chips.length || !more || selector.clientWidth <= 0) return;

  chips.forEach((chip) => { chip.hidden = false; });
  more.hidden = true;

  const gap = Number.parseFloat(getComputedStyle(selector).columnGap) || 7;
  const widths = chips.map((chip) => chip.getBoundingClientRect().width);
  const fullWidth = widths.reduce((sum, width) => sum + width, 0)
    + Math.max(0, widths.length - 1) * gap;
  if (fullWidth <= selector.clientWidth) return;

  more.textContent = `+${chips.length}`;
  more.hidden = false;
  let used = more.getBoundingClientRect().width;
  let visibleCount = 0;

  for (let index = 0; index < widths.length; index += 1) {
    if (used + gap + widths[index] > selector.clientWidth) break;
    used += gap + widths[index];
    visibleCount += 1;
  }

  chips.forEach((chip, index) => { chip.hidden = index >= visibleCount; });
  const hiddenCount = chips.length - visibleCount;
  more.textContent = `+${hiddenCount}`;
  more.setAttribute("aria-label", `Mostra tutti i gironi, ${hiddenCount} non visibili`);
  more.title = `${hiddenCount} gironi non visibili`;
}

function closeGroupsDrawer({ restoreFocus = true } = {}) {
  const drawer = document.querySelector("#groups-drawer");
  if (!restoreFocus) activeDrawerTrigger = null;
  if (drawer?.open) drawer.close();
}

export function stopChampionshipsView() {
  selectorsObserver?.disconnect();
  selectorsObserver = undefined;
  closeGroupsDrawer({ restoreFocus: false });
}

function setupGroupsInterface(competitions) {
  const selectors = Array.from(document.querySelectorAll("[data-group-selector]"));
  const drawer = document.querySelector("#groups-drawer");
  const drawerTitle = drawer?.querySelector("#groups-drawer-title");
  const drawerDescription = drawer?.querySelector("#groups-drawer-description");
  const drawerList = drawer?.querySelector("[data-groups-drawer-list]");
  const search = drawer?.querySelector("[data-groups-search]");
  if (!drawer || !drawerTitle || !drawerDescription || !drawerList || !search) return;

  const fitAll = () => selectors.forEach(fitGroupSelector);
  selectorsObserver?.disconnect();
  selectorsObserver = new ResizeObserver(fitAll);
  selectors.forEach((selector) => selectorsObserver.observe(selector));
  requestAnimationFrame(fitAll);

  const filterDrawer = () => {
    const query = normalizeSearch(search.value);
    const options = Array.from(drawerList.querySelectorAll("[data-group-drawer-option]"));
    let visible = 0;
    options.forEach((option) => {
      const matches = !query || normalizeSearch(option.textContent).includes(query);
      option.hidden = !matches;
      if (matches) visible += 1;
    });
    drawerList.querySelector("[data-groups-empty]")?.toggleAttribute("hidden", visible > 0);
  };

  const openDrawer = (trigger) => {
    const competition = competitions[Number(trigger.dataset.competitionIndex)];
    if (!competition) return;
    activeDrawerTrigger = trigger;
    drawerTitle.textContent = `Gironi (${competition.groups.length})`;
    drawerDescription.textContent = competition.title;
    drawerList.innerHTML = `${competition.groups.map((group) => `
      <a class="groups-drawer-option" href="/gironi/${encodeURIComponent(group.id)}" data-link data-group-drawer-option>
        <strong>${escapeHtml(group.subtitle)}</strong>
        <span>${escapeHtml(group.description)}</span>
        <span class="groups-drawer-arrow" aria-hidden="true">→</span>
      </a>`).join("")}
      <p class="groups-drawer-empty" data-groups-empty role="status" hidden>Nessun girone trovato.</p>`;
    search.value = "";
    trigger.setAttribute("aria-expanded", "true");
    drawer.showModal();
    requestAnimationFrame(() => search.focus());
  };

  document.querySelectorAll("[data-groups-more]").forEach((button) => {
    button.addEventListener("click", () => openDrawer(button));
  });
  drawer.querySelector("[data-groups-drawer-close]")?.addEventListener("click", () => drawer.close());
  drawer.addEventListener("click", (event) => {
    if (event.target === drawer) drawer.close();
  });
  drawer.addEventListener("close", () => {
    activeDrawerTrigger?.setAttribute("aria-expanded", "false");
    activeDrawerTrigger?.focus();
    activeDrawerTrigger = null;
  });
  search.addEventListener("input", filterDrawer);
}

export async function championshipsView(params, signal) {
  stopChampionshipsView();
  const { committee } = params;
  setSelectedCommittee(committee);
  document.title = "Campionati | FIPAV Online";
  document.querySelector("#app").innerHTML = shell(
    pageHeading("Tutti i campionati", "", `/live?comi=${committee}`) +
    `<div id="groups-content">${statusPanel("Caricamento campionati…")}</div>`, { committee },
  );
  hydrateCommitteeIdentity(api, committee, signal);
  try {
    const payload = await api.groups(committee, signal);
    const groups = Array.isArray(payload.data) ? payload.data : [];
    const competitions = new Map();
    groups.forEach((group) => {
      const subtitle = String(group["sub-title"] || "").trim();
      const fullTitle = String(group.title || "Campionato").trim();
      const suffix = subtitle ? ` - ${subtitle}` : "";
      const title = suffix && fullTitle.toLocaleLowerCase().endsWith(suffix.toLocaleLowerCase())
        ? fullTitle.slice(0, -suffix.length)
        : fullTitle;
      const key = title.toLocaleLowerCase();
      if (!competitions.has(key)) competitions.set(key, { title, groups: [] });
      competitions.get(key).groups.push({
        id: group.id,
        subtitle: subtitle || "Girone",
        description: title,
      });
    });

    const competitionList = Array.from(competitions.values());
    competitionList.forEach((competition) => {
      competition.groups.sort((first, second) => first.subtitle.localeCompare(second.subtitle, "it", {
        numeric: true,
        sensitivity: "base",
      }));
    });
    const markup = competitionList.map((competition, competitionIndex) => `<article class="group-card">
      <div class="group-card-copy"><h2>${escapeHtml(competition.title)}</h2></div>
      <nav class="group-selector" data-group-selector aria-label="Gironi di ${escapeHtml(competition.title)}">
        ${competition.groups.map((group) => `<a href="/gironi/${group.id}" data-link data-group-chip aria-label="Apri ${escapeHtml(group.subtitle)} di ${escapeHtml(competition.title)}">${escapeHtml(group.subtitle)}</a>`).join("")}
        <button class="group-more-button" type="button" data-groups-more data-competition-index="${competitionIndex}" aria-haspopup="dialog" aria-expanded="false" hidden></button>
      </nav>
    </article>`).join("");

    document.querySelector("#groups-content").innerHTML = `<section class="container group-list">${markup || statusPanel("Nessun campionato pubblico disponibile.", "empty")}</section>
      <dialog class="groups-drawer" id="groups-drawer" aria-labelledby="groups-drawer-title" aria-describedby="groups-drawer-description">
        <div class="groups-drawer-shell">
          <header class="groups-drawer-header">
            <div><h2 id="groups-drawer-title">Gironi</h2><p id="groups-drawer-description"></p></div>
            <button type="button" class="groups-drawer-close" data-groups-drawer-close aria-label="Chiudi elenco gironi">×</button>
          </header>
          <div class="groups-drawer-search">
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7"></circle><path d="m20 20-4-4"></path></svg>
            <label class="sr-only" for="groups-search">Cerca girone</label>
            <input id="groups-search" type="search" data-groups-search placeholder="Cerca per girone o descrizione…" autocomplete="off">
          </div>
          <nav class="groups-drawer-list" data-groups-drawer-list aria-label="Tutti i gironi"></nav>
        </div>
      </dialog>`;
    setupGroupsInterface(competitionList);
    signal.addEventListener("abort", stopChampionshipsView, { once: true });
  } catch (error) {
    if (error.name !== "AbortError") document.querySelector("#groups-content").innerHTML = statusPanel(errorMessage(error), "error");
  }
}
